import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { listAgents, createAgent, type Agent, type CreateAgentInput } from "../api/client.js";

export function AgentListPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    loadAgents();
  }, []);

  async function loadAgents() {
    try {
      const res = await listAgents();
      setAgents(res.agents);
    } catch {
      // handled silently
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(data: CreateAgentInput) {
    const agent = await createAgent(data);
    setAgents((prev) => [agent, ...prev]);
    setShowCreate(false);
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-ink-100 bg-white px-6">
        <h1 className="text-sm font-semibold text-ink-900">Agents</h1>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-xs">
          <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
          New Agent
        </button>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-6">
          {loading ? (
            <LoadingSkeleton />
          ) : agents.length === 0 ? (
            <EmptyState onAction={() => setShowCreate(true)} />
          ) : (
            <div className="grid gap-3">
              {agents.map((agent) => (
                <AgentCard key={agent.id} agent={agent} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <CreateAgentModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

function AgentCard({ agent }: { agent: Agent }) {
  return (
    <Link
      to={`/agents/${agent.id}`}
      className="card flex items-center gap-4 px-5 py-4 transition-shadow hover:shadow-card"
    >
      {/* Avatar */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-50">
        <span className="text-sm font-semibold text-accent-700">
          {agent.name.charAt(0).toUpperCase()}
        </span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink-900">{agent.name}</span>
          <span className={agent.enabled ? "badge-green" : "badge-gray"}>
            {agent.enabled ? "Active" : "Disabled"}
          </span>
        </div>
        {agent.description && (
          <p className="mt-0.5 truncate text-xs text-ink-500">{agent.description}</p>
        )}
        <div className="mt-1.5 flex items-center gap-3 text-2xs text-ink-400">
          <span className="font-mono">{agent.model}</span>
          <span>{agent.toolPolicyProfile}</span>
        </div>
      </div>

      {/* Chevron */}
      <svg className="h-4 w-4 shrink-0 text-ink-300" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 4l4 4-4 4" />
      </svg>
    </Link>
  );
}

function EmptyState({ onAction }: { onAction: () => void }) {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-2">
          <svg className="h-6 w-6 text-ink-400" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="8" cy="5" r="3" />
            <path d="M2 14c0-3 2.7-5 6-5s6 2 6 5" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-ink-800">No agents yet</h2>
        <p className="mt-1 text-xs text-ink-500">Create your first agent to get started</p>
        <button onClick={onAction} className="btn-primary mt-4 text-xs">
          Create Agent
        </button>
      </div>
    </div>
  );
}

function CreateAgentModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (data: CreateAgentInput) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [model, setModel] = useState("gpt-4");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !systemPrompt.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || undefined,
        systemPrompt: systemPrompt.trim(),
        model,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create agent");
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-ink-950/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative mx-4 w-full max-w-lg animate-slide-up rounded-2xl bg-white p-6 shadow-elevated">
        <h2 className="text-base font-semibold text-ink-900">New Agent</h2>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-700">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="e.g. Research Assistant"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-700">Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input"
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-700">Model</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="input"
            >
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-4-turbo">GPT-4 Turbo</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              <option value="claude-3-opus">Claude 3 Opus</option>
              <option value="claude-3-sonnet">Claude 3 Sonnet</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-700">System Prompt</label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              className="input min-h-[6rem] resize-y font-mono text-xs"
              placeholder="You are a helpful assistant that..."
              required
            />
          </div>

          {error && (
            <p className="text-xs text-danger">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary text-xs">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim() || !systemPrompt.trim()}
              className="btn-primary text-xs"
            >
              {submitting ? "Creating..." : "Create Agent"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="card animate-pulse px-5 py-4">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 rounded-full bg-surface-2" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-32 rounded bg-surface-2" />
              <div className="h-2.5 w-48 rounded bg-surface-2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M8 3v10M3 8h10" />
    </svg>
  );
}
